<template>
<div class="match"
    :class="{
        'clickable': clickable,
        'selected': selected
    }"
    @click="onClickCard">

    <div v-if="ledAlternative" class="crown">
        {{ $t("led_alternative") }}
    </div>

    <div class="top" 
        :class="{ 
            'top-border': !ledAlternative,
            'bottom-border': hasHiddenFields || !card.product_url,
            'bottom-padding': !card.product_url && !hasHiddenFields
        }">
        <div class="match__heading">
            <div class="item-image" :class="{small: !hasImage(card)}" :style="{backgroundImage: `url(${getImageURL(card)})`}"></div>
            <div>{{title}}</div>
        </div>

        <div v-if="card.wiring_diagram && (card.is_perfect_fit || card.is_alternative_fit)">{{ `${$t('wiring_diagram')}: ${card.wiring_diagram}`}} </div>
            
        <div v-if="compPropertiesTop.length !== 0" class="props-group">
            <div v-for="index in compPropertiesTop" :key="index">
                <span v-if="card[index]" class="prop-name">{{ $t(index) }}: </span> 
                <span v-if="card[index] && index !== 'product_code'" class="value">{{ card[index] || "-" }}</span>
                <span v-if="card[index] && index === 'product_code'" class="value">{{ product_code || "-" }}</span>
            </div>
            <div v-if="local_eoc.length > 0">
                <span class="prop-name">{{ $t("local_eoc") }}: </span> 
                <span class="value">{{ local_eoc }}</span>
            </div>
        </div>

        <div v-if="shownFieldList.length !== 0" class="props-group">
            <div v-for="index in shownFieldList" :key="index">
                <span class="prop-name">{{ $t(index) }}: </span> 
                <span class="value">{{ addUnits(index, card[index]) || "-" }}</span>
            </div>
        </div>

        <div v-if="hasHiddenFields" class="hidden-properties-head" 
                :class="{'open': showMore}"
                @click.stop="toggleShowMore">
            <span v-if="!showMore">{{$t("card_show_more")}}</span>
            <span v-if="showMore">{{$t("card_show_less")}}</span>
            <span class="arrow">▲</span>
        </div>
    </div>

    <div v-if="hasHiddenFields" class="middle" :class="{'open': showMore}">
        <div v-for="index in hiddenFieldList" :key="index">
            <span class="prop-name">{{ $t(index) }}: </span> 
            <span class="value">{{ addUnits(index, card[index]) || "-" }}</span>
        </div>
    </div>

    <div v-if="card.product_url /*|| hasHiddenFields*/" class="bottom" :class="{'top-margin': card.product_url}">
        <a v-if="card.product_url" :href="product_url" target="_blank" @click="trackButtonClick(card.ean)" class="btn btn-primary btn-block">{{ $t('card_action')}}</a>
    </div>
    
</div>
</template>



<script>
import { flattenObject, getImageURL, isLed, isLamp, addUnits } from '@/services/utils';
import propertyOrder from '@/services/propertyorder';
import { hasImage } from '../services/utils';

export default {
    name: "MatchCard",
    data() {
        return {
            // These are always shown, and are separated from the other properties
            propertiesTop: [
                "ean",
                "product_code",
                "short_eoc"
            ]
        }
    },
    
    props: {
        showMore: Boolean,
        match: Object,
        meta: Object,
        type: String,
        index: Number,
        hiddenFields: Array,
        ledAlternative: Boolean,
        clickable: {
            type: Boolean,
            default: false
        },
        selected: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        product_code() {
            // Handle lamp variants by removing the variant id
            if (this.card.product_code.search("#") == -1)
                return this.card.product_code;
            return this.card.product_code.split("#")[0];
        },

        product_url() {
            let country = this.$store.state.search.country;
            if (country in this.$store.state.search.catalogueUrlDictionary) {
                let e = this.$store.state.search.catalogueUrlDictionary[country];
                return this.card.product_url.replace("\.com\/", `.${e}/`);
            }
            return this.card.product_url;
        },

        compPropertiesTop() {
            if (isLamp(this.card) && isLed(this.card)) {
                let props = [...this.propertiesTop, "family"];
                return props;
            }

            if (this.card["brand"] == 'Ledvance' || this.card["brand"] == 'Ledvance, Osram')
                return this.propertiesTop.filter(p => p !== 'product_code');

            return this.propertiesTop;
        },

        // The local EOC is only shown if it differs from the global EOC
        local_eoc() {
            if (this.card["local_eoc"] !== this.card["eoc_global"])
                return this.card["local_eoc"];
            return "";
        },
        
        title() {
            // Drivers
            if (isLamp(this.card)) {
                if (isLed(this.card)) 
                    return this.card.material_description;
                return this.card.material_description;
            } else {
                if (isLed(this.card))
                    return this.card.driver_type;
                return this.card.product_description;
            }
        },

        propertyList() {
            if (isLamp(this.card))
                return this.$store.state.search.cardProperties[this.card.lamp_type.toLowerCase()];
            return this.$store.state.search.cardProperties[this.card.dr_type.toLowerCase()];
        },

        hasHiddenFields() {
            return this.hiddenFieldList.length > 0;
        },

        hiddenFieldList() {
            return this.propertyList.filter((e) => {
                return e !== 'brand' && 
                    this.shownFieldList.indexOf(e) == -1 &&
                    this.match.hasOwnProperty(e) &&
                    this.match[e] !== null &&
                    this.match[e] !== 'undefined' &&
                    this.match[e].length !== 0;
            });
        },

        shownFieldList() {
            return this.propertyList.filter((e) => {
                return this.hiddenFields.indexOf(e) == -1 &&
                    this.match.hasOwnProperty(e) &&
                    this.match[e] !== null &&
                    this.match[e] !== 'undefined' &&
                    this.match[e].length !== 0;
            });
        },

        existingPropertyList() {
            let list = [];
            let c = this.card;
            for (let p of this.propertyList)
                if (c[p])
                    list.push(p);
            return list;
        },

        existingPropTopList() {
            let list = [];
            let c = this.card;
            for (let p of this.propertiesTop)
                if (c[p])
                    list.push(p);
            return list;
        },

        existingPropMiddleList() {
            let list = [];
            let c = this.card;
            for (let p of this.propertiesMiddle)
                if (c[p])
                    list.push(p);
            return list;
        },

        existingPropBottomList() {
            let list = [];
            let c = this.card;
            for (let p of this.propertiesBottom)
                if (c[p])
                    list.push(p);
            return list;
        },

        card() {
            const output = flattenObject(this.match);
            
            function flattenArraysInObj(obj) {
                Object.keys(obj).map(key => {
                    if(Array.isArray(obj[key])) {
                    obj[key] = obj[key].join(", ");
                    }
                });
                return obj;
            }

            return flattenArraysInObj(output);
        },
        filteredKeys() {
            const blacklists = new Set([...this.blackListedProperties, ...this.meta.commonEmptyProps]);

            return propertyOrder
                    .filter(orderedProperty => !blacklists.has(orderedProperty))
                    .filter(orderedProperty => Object.keys(this.card).includes(orderedProperty));
        }
    },
    
    methods: {
        onClickCard() {
            if (!this.clickable)
                return;
            this.$emit('clicked', this.card.product_code);
        },

        trackButtonClick(ean){
            omniture.trackConversion("catalogue:" + ean + ":button");
        },

        toggleShowMore() {
            this.$emit("toggleShowMore");
        },

        getImageURL(product) {
            return getImageURL(product);
        },

        addUnits(facet, value) {
            return addUnits(this.$store.state.search.unitDictionary, facet, value);
        },

        hasImage(product) {
            return hasImage(product);
        }
    }
}
</script>



<style lang="scss" scoped>
.clickable {
    cursor: pointer;
}

.selected {
    background-color: #F7F7F7;
}

.match {
    >div { position: relative; }

    >.crown {
        background-color: $nc-blue-primary;
        color: white;
        font-size: $nc-fsz-l;
        text-align: center;
        border: 1px solid $gray;
        border-bottom: none;
    }

    >.top {
        border: 1px solid $gray;
        border-bottom: none;
        border-top: none;
        padding: 20px;
        padding-bottom: 0;

        &.bottom-border { border-bottom: 1px solid $gray; }
        &.bottom-padding { padding-bottom: 20px; }
        &.top-border { border-top: 1px solid $gray; }
    }

    >.middle, >.bottom {
        border: 1px solid $gray;
        border-top: none;
    }

    >.middle {
        display: none;
        background-color: $nc-gray-bg;
        padding: 1em 20px;
    }
    >.middle.open { display: block; }

    >.bottom {
        padding: 0 20px 20px 20px;

        &.top-margin {
            padding-top: 1em;
        }
    }
    width: 220px;
    font-size: $nc-fsz-s;

    &__topbar {
        display: flex;
        grid-row: 1/2;
        transform: translateY(1px);
        height: 52px;

        span {
            height: 100%;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: $dark-blue;
            color: $white;
            font-weight: 500;
            text-align: center;
        }
    }

    &__body {
        grid-row: 2/3;
        display: flex;
        flex-direction: column;
        padding: 20px;
        background-color: $white;
        border: 1px solid $gray;
    }

    .show-more {
        flex: 1 1 auto;
    }

    &__heading {
        display: flex;
        align-items: top;

        >.item-image {
            margin-right: 20px;
            border: $nc-gray-border;
            width: 48px;
            height: 48px;
            max-width: 48px;
            max-height: 48px;
            background-size: contain;
            background-position: center;
            background-repeat: no-repeat;

            &.small {
                background-size: 80%;
            }
        }

        >img {
            width: 48px;
            height: 48px;
            margin-right: 10px;
            display: block;
            flex-basis: 48px;
        }

        >div {
            display: flex;
            align-items: center;
            flex: 1;
            min-height: 48px;
        }
    }

    &__basic-info {
        margin-top: 1em;
    }

    &__description {
        margin-top: 1em;
        flex-grow: 1;
        font-size: $nc-fsz-s;

        .props-group {
            margin-bottom: 1em;
        }

        .prop-name {
            font-weight: bold;
        }

        .remark {
            margin-top: 2em;
        }
    }

    &__specs {
        grid-row: 3/4;
        padding: 20px;
        background-color: $white;
        border-right: 1px solid $gray;
        border-left: 1px solid $gray;

        &:last-child {
            border-bottom: 1px solid $gray;
        }
    }

    &__specs div {
        display: flex;
        font-size: $small-font-size;
        font-weight: 300;
        color: $medium-gray;

        > span {
            width: 50%;
            padding: 1px 0;
        }

        .value {
            color: $dark-gray;

            &.empty {
                color: $medium-gray;
            }

            &.different {
                color: $orange;
                font-weight: $font-small-bold;
            }
        }
    }

    &__specs__toggle {
        display: block;
        position: relative;
        width: 100%;
        padding: 0 1.2rem 0 0;
        margin-top: 10px;
        border: 0;
        background-color: transparent;
        color: $medium-blue;
        font-size: $tiny-font-size;
        text-align: right;

        &:focus,
        &:active,
        &:focus:active {
            outline: none;
            color: $dark-blue;
        }

        &:after {
            content: "";
            position: absolute;
            right: 3px;
            top: 5px;
            display: block;
            background: url('../assets/svg/caret.svg') no-repeat;
            width: 12px;
            height: 12px;
            color: #79B9D3;
        }

        &.open:after {
            transform: rotate(180deg);
            top: 0;
        }
    }

    &__remark {
        margin-top: 1em;
    }

    &__action {
        margin-top: 1em;
    }

    &:last-child {
        .match__body {
            border: 1px solid $dark-blue;


            &.show-more {
                border-bottom-color: $gray;
            }
        }

        .match__specs {
            border-right-color: $dark-blue;
            border-bottom-color: $dark-blue;
            border-left-color: $dark-blue;
        }
    }

    // Hidden fields
    .hidden-properties-head {
        text-align: right;
        font-size: $nc-fsz-xs;
        color: $nc-blue-primary;
        cursor: pointer;
        position: relative;
        padding: 0 10px 0 0;
        margin-top: 1em;

        .arrow {
            display: block;
            position: absolute;
            top: 2px;
            right: 0;
            font-size: 7px;
            transform: rotate(180deg);
        }

        &.open {
            .arrow {
                transform: none;
            }
        }
    }
}

.props-group {
    margin-top: 1em;
}

.prop-name {
    font-weight: bold;
}
</style>
